import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, Grid, Typography } from "@mui/material";
import HeaderForm from "../../components/Header";
import Loader from "../../components/Loader";
import {
  fetchWarehouseHomeData,
  fetchWarehouseDashboardStats,
} from "../../store/slices/homeSlice";
import { setFilters } from "../../store/slices/filterSlice";
import { errorMessage } from "../../components/Toaster";
import { formatDateToISOString } from "../../utils/utils";
import { getErrorMessage } from "../../utils/error";
import { VISO_SUPER_ADMIN } from "../../constants";

const hourLabels = ["12a", "2a", "4a", "6a", "8a", "10a", "12p", "2p", "4p", "6p", "8p", "10p"];

const getLocalDateString = (d = new Date()) => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const isDateStale = (date) => !!date && date < getLocalDateString();

const StatCard = ({ title, value, children }) => (
  <Box
    sx={{
      background: "#fff",
      borderRadius: "16px",
      border: "1px solid #e8ecf4",
      p: "22px 24px",
      height: "100%",
    }}
  >
    <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#64748b", mb: 1.5 }}>
      {title}
    </Typography>
    <Typography sx={{ fontSize: 38, fontWeight: 800, color: "#0f172a", lineHeight: 1 }}>
      {value}
      <Typography component="span" sx={{ fontSize: 15, fontWeight: 500, color: "#94a3b8", ml: 1 }}>
        units
      </Typography>
    </Typography>
    {children && <Box sx={{ mt: 2 }}>{children}</Box>}
  </Box>
);

const GateCard = ({ gate }) => (
  <Box
    sx={{
      background: "#f8fafc",
      borderRadius: "12px",
      border: "1px solid #eef1f6",
      p: "18px 20px",
    }}
  >
    <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1, mb: 1 }}>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 15, fontWeight: 700, color: "#1e293b", whiteSpace: "nowrap" }}>
          {gate.gate_name}
        </Typography>
        <Typography sx={{ fontSize: 12, fontWeight: 500, color: "#94a3b8", whiteSpace: "nowrap" }}>
          {gate.unit_name}
        </Typography>
      </Box>
    </Box>
    <Typography sx={{ fontSize: 30, fontWeight: 800, color: "#0f172a", lineHeight: 1.2 }}>
      {gate.units_in_range || 0}
      <Typography component="span" sx={{ fontSize: 13, fontWeight: 500, color: "#94a3b8", ml: 1 }}>
        units in range
      </Typography>
    </Typography>
  </Box>
);

const HeatRow = ({ label, color, values }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
    <Typography sx={{ width: 92, fontSize: 14, fontWeight: 700, color, flexShrink: 0 }}>
      {label}
    </Typography>
    <Box sx={{ display: "flex", gap: "8px", flex: 1 }}>
      {values.map((v, i) => {
        const max = Math.max(...values, 1);
        const intensity = v === 0 ? 0 : 0.15 + (v / max) * 0.5;
        return (
          <Box
            key={i}
            sx={{
              flex: 1,
              height: 40,
              borderRadius: "8px",
              background: v === 0 ? "#f1f5f9" : `rgba(59,130,246,${intensity})`,
              border: v === 0 ? "1px solid #e2e8f0" : "none",
            }}
          />
        );
      })}
    </Box>
    <Typography sx={{ width: 28, fontSize: 14, fontWeight: 700, color: "#0f172a", textAlign: "right" }}>
      {values.reduce((a, b) => a + b, 0)}
    </Typography>
  </Box>
);

const WarehouseHome = () => {
  const dispatch = useDispatch();
  const role = localStorage.getItem("role_name");
  const isVisoAdmin = role === VISO_SUPER_ADMIN;
  const localCompanyId = localStorage.getItem("company_id");

  const filters = useSelector((state) => state.filters);
  const companyId = isVisoAdmin
    ? filters.selectedCompanyId || localCompanyId
    : localCompanyId;

  const warehouseState = useSelector((state) => state.home.warehouse);
  const {
    warehouseOptions,
    gateOptions,
    gateOverview,
    cargoSummary,
    throughput,
    isLoading,
    loaded,
  } = warehouseState;

  const [gateIds, setGateIds] = useState([]);

  // ── Initial load: fetch warehouses/gates, then stats using the SAME
  //    filters.startDate / filters.endDate / filters.yards / filters.gates
  //    keys that HeaderForm itself reads — matching how Yard works.
  useEffect(() => {
    if (!companyId) return;
    if (loaded) return;

    dispatch(fetchWarehouseHomeData({ companyId }))
      .unwrap()
      .then(({ allGateIds, defaultDates }) => {
        const safeStartDate = filters.startDate || defaultDates.startDate;
        const safeEndDate = isDateStale(filters.endDate)
          ? defaultDates.endDate
          : filters.endDate || defaultDates.endDate;

        const persistedGates =
          filters.gates?.length > 0
            ? filters.gates.filter((id) => allGateIds.includes(id))
            : [];
        const gatesToUse = persistedGates.length > 0 ? persistedGates : allGateIds;

        setGateIds(gatesToUse);

        dispatch(
          setFilters({
            gates: gatesToUse,
            startDate: safeStartDate,
            endDate: safeEndDate,
          })
        );

        dispatch(
          fetchWarehouseDashboardStats({
            gateIds: gatesToUse,
            startDate: formatDateToISOString(safeStartDate, true),
            endDate: formatDateToISOString(safeEndDate, false),
          })
        ).catch((err) => {
          errorMessage(getErrorMessage(err) || "Error fetching dashboard stats.");
        });
      })
      .catch((err) => {
        errorMessage(getErrorMessage(err) || "Something went wrong.");
      });
  }, [companyId, loaded]);

  const handleSubmit = (data) => {
    const selectedGateIds = data.gates?.length ? data.gates : gateIds;
    setGateIds(selectedGateIds);

    dispatch(
      setFilters({
        gates: selectedGateIds,
        startDate: data.startDate,
        endDate: data.endDate,
      })
    );

    dispatch(
      fetchWarehouseDashboardStats({
        gateIds: selectedGateIds,
        startDate: formatDateToISOString(data.startDate, true),
        endDate: formatDateToISOString(data.endDate, false),
      })
    )
      .unwrap()
      .catch((err) => {
        errorMessage(getErrorMessage(err) || "Error fetching dashboard stats.");
      });
  };

  const buildThroughputSeries = (key) => {
    const byHour = Object.fromEntries(throughput.map((r) => [r.hour_bucket, r]));
    return [0, 2, 4, 6, 8, 10, 12, 14, 16, 18, 20, 22].map((h) => byHour[h]?.[key] || 0);
  };
  const loadingSeries = buildThroughputSeries("loading_count");
  const unloadingSeries = buildThroughputSeries("unloading_count");
  const totalSeries = buildThroughputSeries("total_count");

  const peakIndex = totalSeries.indexOf(Math.max(...totalSeries));
  const peakLabel = totalSeries.every((v) => v === 0) ? "—" : hourLabels[peakIndex];

  return (
    <>
      {isLoading ? (
        <Loader color="primary" size={40} />
      ) : (
        <>
          <HeaderForm
            yardOptions={warehouseOptions}
            siteLabel="Warehouse"
            gateOptions={gateOptions}
            showGateDropdown
            onSubmit={handleSubmit}
          />

          <Grid container spacing={3} sx={{ mt: 2.5 }}>
            <Grid size={{ xs: 12, md: 5 }}>
              <Box
                sx={{
                  background: "#fff",
                  borderRadius: "20px",
                  border: "1px solid #e8ecf4",
                  p: "24px 26px",
                  height: "100%",
                }}
              >
                <Typography sx={{ fontSize: 18, fontWeight: 700, color: "#1e293b" }}>
                  Gate Status Overview
                </Typography>
                <Typography sx={{ fontSize: 13.5, color: "#94a3b8", mb: 2.5 }}>
                  Live status for all configured gates.
                </Typography>
                <Grid container spacing={2}>
                  {gateOverview.length > 0 ? (
                    gateOverview.map((gate) => (
                      <Grid size={6} key={gate.gate_id}>
                        <GateCard gate={gate} />
                      </Grid>
                    ))
                  ) : (
                    <Grid size={12}>
                      <Typography sx={{ fontSize: 13, color: "#94a3b8", textAlign: "center", py: 4 }}>
                        No gates found for this filter
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Box>
            </Grid>

            <Grid size={{ xs: 12, md: 7 }}>
              <Grid container spacing={2.5}>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <StatCard title="Total Cargo" value={cargoSummary.total_cargo}>
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                      <Typography sx={{ fontSize: 12, color: "#94a3b8" }}>
                        {cargoSummary.loaded} loaded
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: "#94a3b8" }}>
                        {cargoSummary.unloaded} unloaded
                      </Typography>
                    </Box>
                  </StatCard>
                </Grid>

                <Grid size={{ xs: 12, sm: 4 }}>
                  <StatCard title="Loaded" value={cargoSummary.loaded} />
                </Grid>

                <Grid size={{ xs: 12, sm: 4 }}>
                  <StatCard title="Unloaded" value={cargoSummary.unloaded} />
                </Grid>
              </Grid>

              <Box
                sx={{
                  background: "#fff",
                  borderRadius: "20px",
                  border: "1px solid #e8ecf4",
                  p: "24px 26px",
                  mt: 2.5,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: 2.5 }}>
                  <Box>
                    <Typography sx={{ fontSize: 18, fontWeight: 700, color: "#1e293b" }}>
                      Hourly Throughput
                    </Typography>
                    <Typography sx={{ fontSize: 13.5, color: "#94a3b8" }}>
                      Activity intensity by task &amp; hour · 12am–12pm (2hr buckets)
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: "right" }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#3b82f6" }}>
                      PEAK · {peakLabel}
                    </Typography>
                    <Typography sx={{ fontSize: 11.5, color: "#94a3b8" }}>Low ■ ■ ■ ■ ■ High</Typography>
                  </Box>
                </Box>

                <HeatRow label="Loading" color="#2563eb" values={loadingSeries} />
                <HeatRow label="Unloading" color="#16a34a" values={unloadingSeries} />
                <HeatRow label="Total" color="#475569" values={totalSeries} />

                <Box sx={{ display: "flex", gap: "8px", pl: "100px" }}>
                  {hourLabels.map((h) => (
                    <Typography
                      key={h}
                      sx={{ flex: 1, fontSize: 12, color: "#94a3b8", textAlign: "center" }}
                    >
                      {h}
                    </Typography>
                  ))}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </>
      )}
    </>
  );
};

export default WarehouseHome;